import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import { AdminStudentGroupService } from '../../../core/services/groups/admin-student-group.service';
import { ProfessorService } from '../../../core/services/professor.service';
import { LearningService } from '../../../core/services/learning-path.service';
import { selectableSubjects } from '../../../shared/utils/selectable-subjects';
import { StudentService } from '../../../core/services/student.service';
import { SwalService } from '../../../core/services/utils/swal.service';
import { AuthService } from '../../../core/services/utils/auth.service';
import { matchesSearch } from '../../../core/utils/search-text';

import {
  StudentGroup,
  StudentGroupMember,
  CreateStudentGroupRequest,
  UpdateStudentGroupRequest,
} from '../../../core/interfaces/student-group';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';

interface ProfessorOption { id: number; fullName: string; }
interface SubjectOption { id: number; name: string; isActive: boolean; }
interface LearningPathOption {
  id: number;
  title: string;
  /** Matieres du parcours : le formulaire y restreint la liste des matieres. */
  subjects: SubjectOption[];
}
interface StudentOption { id: number; fullName: string; code?: string | null; }

@Component({
  selector: 'app-admin-groups',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ErrorStateComponent],
  templateUrl: './admin-groups.component.html',
  styleUrl: './admin-groups.component.css',
})
export class AdminGroupsComponent implements OnInit {
  private readonly groupService = inject(AdminStudentGroupService);
  private readonly profService = inject(ProfessorService);
  private readonly pathService = inject(LearningService);
  private readonly studentService = inject(StudentService);
  private readonly swal = inject(SwalService);
  private readonly auth = inject(AuthService);

  /**
   * Le vivier d'élèves d'un parcours sert à ajouter un membre : le serveur ne
   * le rend qu'à qui peut composer un groupe (`groups.write`) ou lire les
   * élèves. Le demander avec la seule lecture des groupes valait un 403 — et,
   * sur une lecture, l'éjection de l'écran à l'ouverture des membres.
   */
  private readonly canListStudents =
    this.auth.hasPermission('groups.write') || this.auth.hasPermission('students.read.all');

  // Data state
  readonly groups = signal<StudentGroup[]>([]);
  readonly loading = signal<boolean>(false);
  readonly errored = signal<boolean>(false);

  readonly professors = signal<ProfessorOption[]>([]);
  readonly paths = signal<LearningPathOption[]>([]);
  readonly professorsErrored = signal<boolean>(false);
  readonly pathsErrored = signal<boolean>(false);

  /**
   * Les listes déroulantes du formulaire (parcours + professeurs) sont
   * indispensables à la création d'un groupe : si elles n'ont pas pu être
   * chargées, on désactive le formulaire au lieu d'afficher de fausses options.
   */
  readonly optionsErrored = computed(
    () => this.professorsErrored() || this.pathsErrored(),
  );

  // Filters
  readonly query = signal<string>('');
  readonly filterPathId = signal<number | null>(null);

  readonly filtered = computed(() => {
    const q = this.query();
    const pid = this.filterPathId();
    return this.groups().filter((g) => {
      if (pid != null && g.learningPathId !== pid) return false;
      // La matière figure dans la colonne « parcours » : la taper doit la trouver.
      return matchesSearch(
        q, g.name, g.description, g.learningPathTitle, g.subjectName, g.professorFullName,
      );
    });
  });

  readonly hasFilters = computed(
    () => this.query().trim().length > 0 || this.filterPathId() !== null,
  );

  // Form modal state (create / edit)
  readonly formOpen = signal<boolean>(false);
  readonly formMode = signal<'create' | 'edit'>('create');
  readonly formGroup = signal<StudentGroup | null>(null);
  readonly formData = signal<{
    name: string; description: string;
    learningPathId: number | null; subjectId: number | null;
    /** Aucun, un, ou plusieurs : une formation hors programme est souvent portée à deux. */
    professorIds: number[];
    isActive: boolean;
  }>({
    name: '', description: '',
    learningPathId: null, subjectId: null, professorIds: [], isActive: true,
  });
  readonly formSubmitting = signal<boolean>(false);

  // Members modal state
  readonly membersOpen = signal<boolean>(false);
  readonly membersGroup = signal<StudentGroup | null>(null);
  readonly members = signal<StudentGroupMember[]>([]);
  readonly availableStudents = signal<StudentOption[]>([]);
  readonly studentToAdd = signal<number | null>(null);
  readonly membersLoading = signal<boolean>(false);
  readonly membersErrored = signal<boolean>(false);

  ngOnInit(): void {
    this.loadGroups();
    this.loadProfessors();
    this.loadPaths();
  }

  // ─── Filters ────────────────────────────────────────────
  onQuery(v: string): void { this.query.set(v); }
  onFilterPath(id: string): void { this.filterPathId.set(id ? +id : null); }
  resetFilters(): void {
    this.query.set('');
    this.filterPathId.set(null);
  }

  // ─── Rechargements manuels (<app-error-state>) ──────────
  /** Recharge la liste des groupes. */
  reload(): void { this.loadGroups(); }

  /** Recharge les listes déroulantes du formulaire. */
  reloadOptions(): void {
    this.loadProfessors();
    this.loadPaths();
  }

  /** Recharge les membres du groupe ouvert dans la modale. */
  reloadMembers(): void {
    const g = this.membersGroup();
    if (g) this.fetchMembers(g.id);
  }

  // ─── Form modal ─────────────────────────────────────────
  openCreate(): void {
    this.formMode.set('create');
    this.formGroup.set(null);
    // Rien de présélectionné : tout est facultatif, et pré-remplir le premier
    // parcours de la liste ferait ranger sous lui une formation qui n'en
    // relève pas.
    this.formData.set({
      name: '', description: '',
      learningPathId: null, subjectId: null, professorIds: [], isActive: true,
    });
    this.formOpen.set(true);
  }

  openEdit(g: StudentGroup): void {
    this.formMode.set('edit');
    this.formGroup.set(g);
    this.formData.set({
      name: g.name,
      description: g.description ?? '',
      learningPathId: g.learningPathId ?? null,
      subjectId: g.subjectId ?? null,
      professorIds: (g.professors ?? []).map((p) => p.professorId),
      isActive: g.isActive ?? true,
    });
    this.formOpen.set(true);
  }

  closeForm(): void { this.formOpen.set(false); }

  patchForm<K extends keyof ReturnType<typeof this.formData>>(key: K, value: any): void {
    this.formData.update((d) => ({ ...d, [key]: value }));
  }

  submitForm(): void {
    // Sans parcours ni professeurs chargés, la création est impossible :
    // on refuse plutôt que d'envoyer des identifiants inventés.
    if (this.formMode() === 'create' && this.optionsErrored()) return;

    const d = this.formData();

    // Seul le nom reste obligatoire : parcours, matière et professeurs sont
    // facultatifs depuis qu'un groupe peut porter une formation hors programme.
    if (!d.name.trim()) {
      this.swal.warning('GROUPS.FORM.MISSING_FIELDS');
      return;
    }

    this.formSubmitting.set(true);

    if (this.formMode() === 'create') {
      const payload: CreateStudentGroupRequest = {
        name: d.name.trim(),
        description: d.description.trim() || null,
        learningPathId: d.learningPathId,
        subjectId: d.subjectId,
        professorIds: d.professorIds,
        isActive: d.isActive,
      };
      this.groupService.create(payload).subscribe({
        next: (res) => {
          if (res?.data) this.groups.set([res.data, ...this.groups()]);
          this.formSubmitting.set(false);
          this.formOpen.set(false);
          this.swal.success('GROUPS.FORM.CREATED');
        },
        error: (err) => {
          console.error(err);
          this.formSubmitting.set(false);
          this.swal.error('GROUPS.FORM.CREATE_ERROR');
        },
      });
    } else {
      const id = this.formGroup()!.id;
      const payload: UpdateStudentGroupRequest = {
        name: d.name.trim(),
        description: d.description.trim() || null,
        isActive: d.isActive,
      };
      this.groupService.update(id, payload).subscribe({
        next: (res) => {
          if (res?.data) this.groups.set(this.groups().map((g) => g.id === id ? res.data : g));
          this.formSubmitting.set(false);
          this.formOpen.set(false);
          this.swal.success('GROUPS.FORM.UPDATED');
        },
        error: (err) => {
          console.error(err);
          this.formSubmitting.set(false);
          this.swal.error('GROUPS.FORM.UPDATE_ERROR');
        },
      });
    }
  }

  // ─── Delete ─────────────────────────────────────────────
  remove(g: StudentGroup): void {
    this.swal.confirm('GROUPS.CONFIRM_DELETE', () => {
      this.groupService.delete(g.id).subscribe({
        next: () => {
          this.groups.set(this.groups().filter((x) => x.id !== g.id));
          this.swal.success('GROUPS.DELETED');
        },
        error: (err) => {
          console.error(err);
          this.swal.error('GROUPS.DELETE_ERROR');
        },
      });
    });
  }

  // ─── Members modal ──────────────────────────────────────
  openMembers(g: StudentGroup): void {
    this.membersGroup.set(g);
    this.members.set([]);
    // La liste des élèves proposables est celle du parcours du groupe ouvert.
    // Sans la vider, un groupe hors programme — pour lequel rien n'est
    // rechargé — proposait les élèves du groupe consulté juste avant.
    this.availableStudents.set([]);
    this.studentToAdd.set(null);
    this.membersOpen.set(true);
    this.fetchMembers(g.id);
    // Sans parcours, il n'y a pas de vivier à proposer : un groupe hors
    // programme se peuple élève par élève, pas depuis une liste de parcours.
    if (g.learningPathId != null && this.canListStudents) this.fetchAvailableStudents(g.learningPathId);
  }

  closeMembers(): void { this.membersOpen.set(false); }

  addMember(): void {
    const sid = this.studentToAdd();
    const grp = this.membersGroup();
    if (!sid || !grp) return;
    this.groupService.addMembers(grp.id, { studentIds: [sid] }).subscribe({
      next: (res) => {
        const returned = res?.data ?? [];
        // The API may return the full updated list or just newly added members.
        // Merge and de-duplicate by studentId to avoid phantom duplicates either way.
        const merged = [...this.members(), ...returned];
        const unique = merged.filter(
          (m, i) => merged.findIndex((x) => x.studentId === m.studentId) === i,
        );
        const previousCount = this.members().length;
        this.members.set(unique);
        this.studentToAdd.set(null);
        this.bumpMemberCount(grp.id, unique.length - previousCount);
      },
      error: (err) => {
        console.error(err);
        this.swal.error('GROUPS.MEMBERS.ADD_ERROR');
      },
    });
  }

  removeMember(m: StudentGroupMember): void {
    const grp = this.membersGroup();
    if (!grp) return;
    this.swal.confirm('GROUPS.MEMBERS.CONFIRM_REMOVE', () => {
      this.groupService.removeMember(grp.id, m.studentId).subscribe({
        next: () => {
          this.members.set(this.members().filter((x) => x.studentId !== m.studentId));
          this.bumpMemberCount(grp.id, -1);
        },
        error: (err) => {
          console.error(err);
          this.swal.error('GROUPS.MEMBERS.REMOVE_ERROR');
        },
      });
    });
  }

  // ─── Helpers ────────────────────────────────────────────
  private bumpMemberCount(groupId: number, delta: number): void {
    this.groups.set(this.groups().map((g) =>
      g.id === groupId ? { ...g, memberCount: Math.max(0, (g.memberCount ?? 0) + delta) } : g,
    ));
  }

  private loadGroups(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.groupService.list().subscribe({
      next: (res) => {
        this.groups.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.groups.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private loadProfessors(): void {
    this.professorsErrored.set(false);
    this.profService.getProfessors().subscribe({
      next: (res: any) => {
        const list = (res?.data ?? []).map((p: any): ProfessorOption => ({
          id: p.id,
          fullName: `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || `#${p.id}`,
        }));
        this.professors.set(list);
      },
      error: () => {
        this.professors.set([]);
        this.professorsErrored.set(true);
      },
    });
  }

  private loadPaths(): void {
    this.pathsErrored.set(false);
    // Les matieres voyagent avec les parcours : le formulaire en propose une,
    // et une seconde requete par changement de parcours n'apporterait rien.
    this.pathService.getReferential().subscribe({
      next: (res: any) => {
        const list = (res?.data ?? []).map((p: any): LearningPathOption => ({
          id: p.id,
          title: p.title ?? `LP #${p.id}`,
          subjects: (p.subjects ?? []).map((sj: any): SubjectOption => ({
            id: sj.id,
            name: sj.name ?? `#${sj.id}`,
            isActive: sj.isActive === true,
          })),
        }));
        this.paths.set(list);
      },
      error: () => {
        this.paths.set([]);
        this.pathsErrored.set(true);
      },
    });
  }

  private fetchMembers(groupId: number): void {
    this.membersLoading.set(true);
    this.membersErrored.set(false);
    this.groupService.getById(groupId).subscribe({
      next: (res) => {
        this.members.set(res?.data?.members ?? []);
        this.membersLoading.set(false);
      },
      error: () => {
        this.members.set([]);
        this.membersErrored.set(true);
        this.membersLoading.set(false);
      },
    });
  }

  private fetchAvailableStudents(learningPathId: number): void {
    this.studentService.getStudentsByLearningPath(learningPathId).subscribe({
      next: (res: any) => {
        // Le dialogue a pu passer à un autre groupe pendant la requête.
        if (this.membersGroup()?.learningPathId !== learningPathId) return;
        const list = (res?.data ?? []).map((s: any): StudentOption => ({
          id: s.id,
          fullName: `${s.firstName ?? ''} ${s.lastName ?? ''}`.trim() || `#${s.id}`,
          code: s.studentCode ?? null,
        }));
        this.availableStudents.set(list);
      },
      error: () => {
        if (this.membersGroup()?.learningPathId === learningPathId) this.availableStudents.set([]);
      },
    });
  }

  isAlreadyMember(studentId: number): boolean {
    return this.members().some((m) => m.studentId === studentId);
  }

  // ─── Lookups for the template ────────────────────────────
  pathTitle(id: number | null | undefined): string {
    if (id == null) return '—';
    return this.paths().find((p) => p.id === id)?.title ?? `#${id}`;
  }
  professorName(id: number | null | undefined): string {
    if (id == null) return '—';
    return this.professors().find((p) => p.id === id)?.fullName ?? `#${id}`;
  }

  /**
   * Encadrants du groupe, séparés par des virgules.
   *
   * <p>La colonne n'affichait que le titulaire : depuis qu'un groupe peut être
   * porté à plusieurs, s'en tenir au premier laisserait croire qu'il est
   * seul.</p>
   */
  professorsLabel(g: StudentGroup): string {
    const names = (g.professors ?? [])
      .map((p) => p.fullName?.trim() || this.professorName(p.professorId))
      .filter((n) => n && n !== '—');

    if (names.length > 0) return names.join(', ');
    return g.professorFullName ?? this.professorName(g.professorId);
  }

  /**
   * Matières proposées pour le parcours choisi ; vide tant qu'aucun parcours
   * ne l'est. Les seules actives — plus celle du groupe qu'on modifie, si elle
   * a été désactivée depuis : la masquer viderait le sélecteur, et
   * l'enregistrement suivant détacherait le groupe de sa matière.
   */
  readonly subjectsOfPath = computed(() => {
    const { learningPathId, subjectId } = this.formData();
    if (learningPathId == null) return [];
    return selectableSubjects(this.paths().find((p) => p.id === learningPathId)?.subjects, [subjectId]);
  });

  isProfessorPicked(id: number): boolean {
    return this.formData().professorIds.includes(id);
  }

  toggleProfessor(id: number, picked: boolean): void {
    this.formData.update((d) => ({
      ...d,
      professorIds: picked
        ? [...d.professorIds, id]
        : d.professorIds.filter((x) => x !== id),
    }));
  }

  /**
   * Change de parcours et écarte la matière retenue.
   *
   * <p>Elle appartenait au parcours précédent : la garder croiserait deux
   * portées et n'aboutirait à rien.</p>
   */
  onPathChange(raw: string): void {
    const learningPathId = raw ? Number(raw) : null;
    this.formData.update((d) => ({ ...d, learningPathId, subjectId: null }));
  }
}
